import { ChevronDown, Info } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAudit } from "../../../../contexts/auditContext";
import { useAuth } from "../../../../contexts/authContext";
import { AnalyticsWorkflowError } from "../../../../lib/analyticsWorkflow";
import { cn } from "../../../../lib/utils";
import { hasPermission } from "../../../../lib/permissions";
import { isTextInputTarget } from "../../../../lib/domGuards";
import { useEvidenceArrowNav } from "../../../../hooks/useEvidenceArrowNav";
import { citationsToViewerBoxes } from "../lib/viewerBoxes";
import { Button } from "../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { useAttributeTestHighlight } from "../../../../hooks/useAttributeTestHighlight";
import { useTrailAddCitation } from "../../../../hooks/useTrailAddCitation";
import { useTrailImportCitation } from "../../../../hooks/useTrailImportCitation";
import {
  type AuditSession,
  type ProcedureColumn,
} from "../../../../models/audit/auditSession";
import { type AnalyticsColumnResult } from "../../../../models/audit/analyticsColumnResult";
import {
  CONTROL_EXCEPTION_DESCRIPTIONS,
  type OutcomeType,
} from "../../../../models/audit/attributeTestColumn";
import { AddCitationModal } from "./AddCitationModal";
import { AgentStepsPanel } from "./attributeTestAnalysis/AgentStepsPanel";
import { analyticsErrorMessage } from "./analyticsResultAnalysis/analyticsErrorMessage";
import { AnalyticsOutputViewerPane } from "./analyticsResultAnalysis/AnalyticsOutputViewerPane";
import { AnalyticsOverrideDialog } from "./analyticsResultAnalysis/AnalyticsOverrideDialog";
import {
  AnalyticsReviewSections,
  NEWER_RESULT_NOTICE,
} from "./analyticsResultAnalysis/AnalyticsReviewSections";
import {
  outcomeBadgeStyle,
  outcomeBadgeValue,
} from "./analyticsResultAnalysis/outcomeBadge";
import { OUTCOME_OPTIONS } from "./analyticsResultAnalysis/outcomeOptions";
import { useAnalyticsOverrideFlow } from "../../../../hooks/audit/useAnalyticsOverrideFlow";
import { VerdictProvenance } from "./analyticsResultAnalysis/VerdictProvenance";
import { DeterministicStepsPanel } from "./attributeTestAnalysis/DeterministicStepsPanel";
import { StepCardViewer } from "./attributeTestAnalysis/StepCardViewer";
import { groupAgentCitationsByStep } from "../lib/agentCitations";
import { useAnalyticsResultData } from "../../../../hooks/audit/useAnalyticsResultData";
import { useCellSupersession } from "../../../../hooks/audit/useCellSupersession";
import { useDeterministicSteps } from "../../../../hooks/audit/useDeterministicSteps";
import { type Citation } from "../../../../models/audit/citation";
import { AuditorTrailPanel } from "./attributeTestAnalysis/AuditorTrailPanel";
import { citationToBoundingBox } from "./attributeTestAnalysis/types";
import { DocumentViewerPanel } from "./attributeTestAnalysis/DocumentViewerPanel";
import { InlineEditableSelect } from "./InlineEditableSelect";
import { buildTrailDisplay, findTrailDisplayItem } from "../lib/trailDisplay";
import { WorkpaperFoldOut } from "./WorkpaperFoldOut";
import { WorkpaperNavGrid } from "./WorkpaperNavGrid";
import { useSessionAnalyticsOwner } from "../../../../hooks/audit/useSessionAnalyticsOwner";
import {
  type AnalyticsOutputFiles,
  useAnalyticsOutputFiles,
} from "../../../../hooks/audit/useAnalyticsOutputFiles";
import { type AnalyticsRunOutputSummary } from "../../../../models/audit/analyticsWorkflow";
import { type DeterministicOperationRow } from "../lib/deterministicSteps";

interface AttributeTestFoldOutProps {
  auditSession: AuditSession;
  checkResultId?: string;
  sourceRowId?: string;
  onClose: () => void;
  /** The work-paper table, rendered as the left pane. Owned by
   * `ProcedureSessionWorkPaper` so its selection wiring (re-targeting) stays
   * in one place. */
  gridPane: ReactNode;
  /** Grid-pane open state, lifted to `ProcedureSessionWorkPaper` so it survives
   * this fold-out's per-cell remount (keeps the workpaper open across clicks). */
  gridExpanded?: boolean;
  onGridExpandedChange?: (expanded: boolean) => void;
  /** Pane proportions, lifted likewise so a resized split survives the remount. */
  paneLayout?: number[] | null;
  onPaneLayoutChange?: (layout: number[]) => void;
  /** Lifted grid scroll offset — see `WorkpaperFoldOut`. */
  gridScrollTopRef?: React.MutableRefObject<number>;
}

const TICKMARKS_KEY = "midship:workpaperTickmarks";

// Agent Steps citations render as uniform, code-less boxes in the doc viewer so
// they read as the model's raw evidence — Midship orange, visually distinct from
// the blue, coded Workpaper Review tickmarks.
const AGENT_BOX_COLOR = "#ea580c"; // Midship orange (tailwind orange-600)

// Workpaper Review (L2) tickmarks all share one colour in the doc viewer. A
// single neutral blue avoids red/green being misread as a pass/fail signal; the
// per-tickmark code (TM 1, TM 2, …) is what distinguishes the steps.
const TRAIL_BOX_COLOR = "#1d4ed8"; // blue (tailwind blue-700)

/**
 * The outcome pill both kinds of column wear.
 *
 * One component rather than one per kind. An attribute-test `pass` and an
 * analytics `pass` are the same answer in the same vocabulary, so a reader who
 * learned the colours on one kind of cell would be reading a second dialect on
 * the other if the two were drawn apart.
 *
 * `interactive` carries the hover tint and nothing else: a pill that cannot be
 * pressed — the verdict superseded, the session closed, the reader unable to
 * write — must not light up under the cursor promising an action it will not
 * perform, while still stating the verdict exactly as a pressable one does.
 */
function OutcomeBadgePill({
  value,
  interactive = false,
}: {
  value: string;
  interactive?: boolean;
}) {
  const { label, classes, hoverClasses } = outcomeBadgeStyle(value);
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5 text-xs font-semibold transition-colors",
        classes,
        interactive && hoverClasses,
      )}
    >
      {label}
    </span>
  );
}

/**
 * The analytics verdict's badge: the same pill, opening the `New Conclusion`
 * override instead of a dropdown.
 *
 * A dropdown would be the wrong control here even though the two kinds of
 * column answer in one vocabulary. An analytics conclusion is an outcome and
 * the auditor's own rationale together — a verdict carrying an answer with no
 * words behind it, or with the judge's words reused, would attribute the
 * judge's reasoning to the auditor — so the only thing a single click can
 * honestly do is open the dialog that asks for both.
 *
 * Where no conclusion can be offered the pill stays exactly as it reads
 * elsewhere and simply stops being a control: the verdict and its tone are what
 * an auditor opened the cell to see, and withholding them to signal a missing
 * affordance would answer the wrong question.
 */
function AnalyticsOutcomeBadge({
  verdict,
  unavailableReason,
  onOverride,
}: {
  verdict: { outcome: OutcomeType | null; isConclusive: boolean };
  unavailableReason: string | null;
  onOverride: () => void;
}) {
  const value = outcomeBadgeValue(verdict);
  const { label } = outcomeBadgeStyle(value);

  if (unavailableReason !== null) {
    return (
      <span className="ml-auto shrink-0" title={unavailableReason}>
        <OutcomeBadgePill value={value} />
      </span>
    );
  }

  return (
    <button
      type="button"
      className="ml-auto shrink-0"
      onClick={onOverride}
      aria-label={`${label} — write a new conclusion`}
    >
      <OutcomeBadgePill value={value} interactive />
    </button>
  );
}

/** Clickable Pass / Fail / N/A (or Inconclusive) outcome badge: a bordered pill
 * that opens a dropdown to change the result's outcome. Selecting persists via
 * `onSelect` (which transitions an inconclusive result to conclusive). */
function OutcomeBadgeSelect({
  outcome,
  isConclusive,
  onSelect,
}: {
  outcome: "pass" | "fail" | "not_applicable" | null;
  isConclusive: boolean;
  onSelect: (value: string) => Promise<void>;
}) {
  const value = outcomeBadgeValue({ outcome, isConclusive });
  return (
    <InlineEditableSelect
      className="ml-auto shrink-0"
      hideChevron
      value={value}
      options={OUTCOME_OPTIONS}
      onSave={async (next) => {
        await onSelect(String(next));
      }}
      renderDisplay={(displayed) => (
        <OutcomeBadgePill value={String(displayed)} interactive />
      )}
    />
  );
}

/**
 * Which view the middle pane is showing.
 *
 * The second member is named for what the pane *is* rather than for who
 * produced it: the same pane lists an agent's reperformance steps for an
 * attribute-test column and a workflow's operations for an analytics one, and
 * the whole premise of an analytics column is that no agent judged anything.
 * A value spelled `"agent"` would have meant "deterministic steps" for half the
 * column kinds it is threaded through.
 */
type MiddlePaneView = "review" | "steps";

/** Which kind of column the fold-out was opened over. */
type ColumnKind = "agent" | "analytics";

/**
 * What the steps view is called, and what it contains, per column kind.
 *
 * The tooltip is not decoration here. "Everything the AI agent did" is not
 * merely the wrong label over a deterministic workflow — it is the wrong claim,
 * asserting a judgment nothing made.
 */
const STEPS_VIEW: Record<ColumnKind, { label: string; info: string }> = {
  agent: {
    label: "Agent Steps",
    info: "Everything the AI agent did",
  },
  analytics: {
    label: "Deterministic Steps",
    info: "Every step the workflow ran, in the order it ran them",
  },
};

/** Full-width view switch for the middle pane: the persisted Auditor Trail
 * ("Workpaper Review") vs the steps beside it. Two equal, boxy buttons sharing
 * one type treatment, each with an info tooltip explaining what the view
 * contains. */
function MiddlePaneViewToggle({
  value,
  onChange,
  columnKind,
}: {
  value: MiddlePaneView;
  onChange: (value: MiddlePaneView) => void;
  columnKind: ColumnKind;
}) {
  const options = [
    {
      key: "review" as const,
      label: "Workpaper Review",
      info: "Annotations that will be downloaded to your excel workpaper",
    },
    { key: "steps" as const, ...STEPS_VIEW[columnKind] },
  ];
  return (
    <TooltipProvider delayDuration={300} disableHoverableContent>
      <div className="flex w-full items-center gap-1.5 text-sm">
        {options.map(({ key, label, info }) => {
          const active = value === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={cn(
                "relative flex-1 rounded-md border px-3 py-1.5 text-center font-semibold transition-all",
                active
                  ? "border-stone-300 bg-white text-stone-900 shadow-sm"
                  : "border-stone-200 bg-stone-50 text-stone-500 hover:bg-white hover:text-stone-700",
              )}
            >
              {label}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    role="img"
                    aria-label={`About ${label}`}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 transition-colors hover:text-stone-600"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[220px]">
                  {info}
                </TooltipContent>
              </Tooltip>
            </button>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

/** Treatment for a review tab that has a sentence to show instead of the run. */
const REVIEW_MESSAGE_CLASS =
  "flex h-full flex-col items-center justify-center gap-3 px-4 text-center text-sm text-stone-400";

/**
 * The `Workpaper Review` tab over an analytics cell.
 *
 * The same review the sheet shows, because it is the same component — a second
 * rendering of these sections would be two accounts of one run, free to drift
 * apart while each claims to be the run's own. What differs is the surround: a
 * pane inside the fold-out rather than a sheet, so the states before the verdict
 * arrives are stated here in the pane's own voice.
 *
 * The activity log stays on the sheet: it is the column's history rather than
 * this run's evidence, and this pane's three columns are already spoken for. A
 * conclusion recorded from the fold-out's own header therefore has no log on
 * screen to go stale against — the sheet reads the column's history afresh
 * whenever it is opened.
 */
function AnalyticsReviewTab({
  auditSession,
  sourceRowId,
  analyticsResult,
  column,
  isLoading,
  issued,
  error,
  onRetry,
  selectedStepId,
  onSelectStep,
  onToggleStar,
  outputFiles,
}: {
  auditSession: AuditSession;
  sourceRowId?: string;
  analyticsResult: AnalyticsColumnResult | undefined;
  column: ProcedureColumn | undefined;
  isLoading: boolean;
  /** The verdict read was enabled, so some fetch of it has been started. */
  issued: boolean;
  error: Error | null;
  onRetry: () => void;
  /**
   * The viewer pane's step and the selection a row offers, carried from the
   * fold-out's own step state rather than held here. The tab beside this one
   * lists the same steps, and a selection of its own would let one tab open a
   * step the other says is not the open one.
   *
   * The star is absent wherever the host cannot write the flag, and the row is
   * then drawn with a read-only one: the step is still in or out of the
   * workpaper, and an auditor who cannot change that still needs told which. The
   * star hands up the card's own citation, which is what its include flag lives
   * on, so the write never waits on the run-steps read a step row is built from.
   */
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
  onToggleStar?: (citation: Citation) => void;
  /** Opens output files in the fold-out's viewer pane, beside this tab. */
  outputFiles: AnalyticsOutputFiles;
}) {
  // A disabled read is byte-identical to one still in flight — no data, not
  // loading, no error — so both wait here. The verdict read stays disabled until
  // the selection names a verdict id, and falling through to the empty arm below
  // would tell the auditor the verdict does not exist over a request that never
  // went out. It waits rather than asserts, and self-recovers when the id lands.
  if (isLoading || !issued) {
    return (
      <div className={REVIEW_MESSAGE_CLASS}>Loading this run's review…</div>
    );
  }

  if (error) {
    // A schema violation re-parses the same payload and fails the same way, so
    // Retry is the one offer known not to clear it. The copy for this kind asks
    // for a refresh instead, and a refresh is what can change the outcome: the
    // row is read again from scratch, under whatever client build is current by
    // then.
    const unreadable =
      error instanceof AnalyticsWorkflowError && error.kind === "unreadable";

    return (
      <div className={REVIEW_MESSAGE_CLASS}>
        {/* Copy authored against the failure kind, never the error's own text:
            an `AnalyticsWorkflowError`'s message is the backend's `detail`
            string verbatim, or a fallback naming the kind to a developer. The
            failure is a signal, not copy. */}
        <span>{analyticsErrorMessage(error)}</span>
        {unreadable ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        ) : (
          /* Retry re-runs the fetch, which clears a transient failure; a
             standing refusal — a deleted workflow, a denied permission —
             answers the same way each time, so this is offered without
             promising it always recovers. */
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    );
  }

  // Reached only once the read has gone out and answered without a verdict: the
  // never-issued and in-flight windows both wait above, so this states absence
  // only where the read has actually established it.
  if (!analyticsResult) {
    return (
      <div className={REVIEW_MESSAGE_CLASS}>No analytics result found.</div>
    );
  }

  if (!column) {
    return (
      <div className={REVIEW_MESSAGE_CLASS}>
        This verdict loaded, but its analytics column is not part of this
        procedure.
      </div>
    );
  }

  return (
    <div className="h-full space-y-6 overflow-y-auto px-4 py-3">
      <AnalyticsReviewSections
        auditSession={auditSession}
        analyticsResult={analyticsResult}
        column={column}
        sourceRowId={sourceRowId}
        selectedStepId={selectedStepId}
        onSelectStep={onSelectStep}
        onToggleStar={onToggleStar}
        outputFiles={outputFiles}
        // In the body rather than the fold-out's header row, which has no room
        // for it.
        provenance={<VerdictProvenance result={analyticsResult} />}
      />
    </div>
  );
}

/**
 * Inline three-pane fold-out for a result cell: `grid | steps | viewer`. Which
 * kind of column it is over is read off the selection's own id namespace, never
 * from who mounted it, so the middle pane's two tabs name what they actually
 * contain.
 *
 * Over an attribute-test cell the middle pane is the persisted Auditor Trail
 * beside the agent's reperformance steps; clicking a step's citation drives the
 * doc viewer (right) via the shared highlight hook. Over an analytics cell it is
 * the verdict's review beside the run's deterministic steps, and the right pane
 * is the step's card or an output file the review opened. Layout/mechanics live in `WorkpaperFoldOut`.
 */
export function AttributeTestFoldOut({
  auditSession,
  checkResultId,
  sourceRowId,
  onClose,
  gridPane,
  gridExpanded,
  onGridExpandedChange,
  paneLayout,
  onPaneLayoutChange,
  gridScrollTopRef,
}: AttributeTestFoldOutProps) {
  // Middle-pane view: the persisted Auditor Trail ("Workpaper Review") or the
  // steps beside it.
  const [middleView, setMiddleView] = useState<MiddlePaneView>("review");

  const {
    auditorTrail,
    attributeTestResult,
    sourceRow,
    columnName,
    attributeColumnCode,
    allCitations,
    isScreenshotDoc,
    navigation,
    actions,
    showDocumentViewer,
    handleCitationClick,
    selectInitialEvidence,
    showCitationView,
  } = useAttributeTestHighlight({
    auditSession,
    checkResultId,
    sourceRowId,
  });

  const { controlSession, setCurrentSelection } = useAudit();
  const analyticsOwner = useSessionAnalyticsOwner();

  // The run's steps, its bookends and its population — idle unless the selection
  // is an analytics verdict, which is also what settles the column kind: an
  // analytics cell is selected under its own id namespace, so the fold-out reads
  // the kind off the selection rather than being told.
  const deterministic = useDeterministicSteps({
    auditSession,
    checkResultId,
    sourceRowId,
    analyticsOwner,
  });
  const columnKind: ColumnKind = deterministic.isAnalytics
    ? "analytics"
    : "agent";

  // The verdict behind an analytics cell, for the review tab. Idle for every
  // other selection (the hook's own read is disabled without an `analytics-id-`
  // namespace) and served from the same cache entry `useDeterministicSteps`
  // already registered, so this adds no request.
  const analytics = useAnalyticsResultData({ auditSession, checkResultId });

  // The review's output files open in the viewer pane rather than the dialog
  // the sheet uses, and the pane is the step card's otherwise: the two take
  // turns, so opening one closes the other. The column id is empty wherever no
  // analytics verdict is loaded, and then no file is listed to open.
  const hostOutputFiles = useAnalyticsOutputFiles(
    analyticsOwner,
    analytics.analyticsResult?.analyticsColumnId ?? "",
    sourceRowId,
  );
  const { clearStep, selectStep: selectStepRow } = deterministic;
  const { closeViewer, viewerClosed, view: viewOutputFile } = hostOutputFiles;
  const selectStep = useCallback(
    (row: DeterministicOperationRow) => {
      closeViewer();
      selectStepRow(row);
    },
    [closeViewer, selectStepRow],
  );
  const outputFiles: AnalyticsOutputFiles = {
    ...hostOutputFiles,
    view: (output: AnalyticsRunOutputSummary, trigger: HTMLElement) => {
      clearStep();
      return viewOutputFile(output, trigger);
    },
  };
  // The pane has no exit animation to wait out, so its close settles at once
  // and hands focus back to the View button, where the pane's own close button
  // would otherwise leave it on the body as it unmounts.
  const closeOutputFilePane = useCallback(() => {
    closeViewer();
    viewerClosed();
  }, [closeViewer, viewerClosed]);

  // The `New Conclusion` override the header pill offers over an analytics
  // cell, driven through the same flow the analytics sheet drives. An override
  // answers under a new id and leaves the row it replaced unwritable, so the
  // retarget is not polish: a fold-out still pointed at the superseded row is
  // refused by every write that follows.
  const analyticsOverride = useAnalyticsOverrideFlow({
    auditSession,
    analyticsResultId: analytics.analyticsResultId,
    analyticsResult: analytics.analyticsResult,
    column: analytics.column,
    checkResultId,
    sourceRowId,
    refetch: analytics.refetch,
    // The successor's selection is written in full, down to the evaluated
    // column a cell click carries: a selection this fold-out leaves less
    // specified than the one clicking that same cell produces is a second
    // shape of "this cell is open" for everything reading the context.
    onRetarget: (selection) =>
      setCurrentSelection({
        procedureSessionId: auditSession.id,
        sourceRowId: selection.sourceRowId,
        checkResultId: selection.checkResultId,
        evalColumnId: selection.evalColumnId,
      }),
    // No activity log is shown here — the sheet is where the column's log
    // lives — so there is nothing on screen a re-read would bring up to date.
  });

  // Add-citation flow: the "Add Citation" button above the doc viewer opens the
  // shared modal in trail mode; the L2 step is chosen in the dialog.
  const trailAddCitation = useTrailAddCitation({
    auditSession,
    attributeTestResult,
    sourceRowId,
    allCitations,
    setCurrentBoundingBox: navigation.setCurrentBoundingBox,
  });

  // Import an agent citation onto a review-trail step (Agent Steps "add to
  // review" affordance), then refetch the trail so Workpaper Review surfaces it.
  const importCitationToReview = useTrailImportCitation({
    auditSessionId: auditSession.id,
    attributeTestResultId: attributeTestResult?.id,
    sourceRowId,
  });
  const reviewStepOptions = useMemo(
    () =>
      (auditorTrail?.steps ?? []).map((step) => ({
        id: step.id,
        text: step.text,
      })),
    [auditorTrail],
  );

  const isConclusive = attributeTestResult?.isConclusive ?? false;
  const outcome =
    attributeTestResult?.isConclusive === true
      ? attributeTestResult.outcome
      : null;

  // The verdict an analytics cell states, read only where the fold-out is over
  // one: the same hook is idle for an attribute-test selection, and a stale
  // entry from a previously-open analytics cell must not badge this one.
  const analyticsVerdict =
    columnKind === "analytics" ? analytics.analyticsResult : undefined;

  // Whether this cell has moved on to a later verdict, from the one reader the
  // review tab consumes too, so the two tabs cannot disagree about it. The
  // steps tab is read live for the cell while the header above still
  // describes `analyticsVerdict`, so once the cell moves on the tab would
  // render the new run's steps under the old verdict's frame; it collapses to a
  // notice instead, the same way the review tab's sections do.
  //
  // `heldVerdict` is `undefined` for any non-analytics selection, so this reads
  // false there and the attribute-test steps tab is never gated by it.
  const cellSuperseded = useCellSupersession({
    procedureSessionId: auditSession.id,
    sourceRowId,
    heldVerdict: analyticsVerdict,
  });

  // The same state the review tab swaps its OUTPUT FILES for: that tab's
  // instance is idle here, so the pane's file would otherwise outlive the list
  // it was opened from.
  const workflowDeleted = deterministic.workflowDeletion.kind === "deleted";

  // A superseded cell's open file is the old run's, and a deleted workflow's
  // can no longer be fetched, so in either state the pane's Download asks for a
  // file the cell no longer offers. Closing also abandons a view still being
  // minted, so a late answer cannot open over the notice. Neither is ever true
  // on an attribute-test column, so this does nothing there.
  useEffect(() => {
    if (cellSuperseded || workflowDeleted) {
      closeViewer();
    }
  }, [cellSuperseded, workflowDeleted, closeViewer]);

  // The answer the header wears, whichever kind of column it is over. Both
  // kinds answer in one vocabulary, so one tone rule serves both; a verdict
  // that reached no answer tints nothing, because none has been given.
  const headerOutcome =
    columnKind === "analytics"
      ? analyticsVerdict?.isConclusive === true
        ? analyticsVerdict.outcome
        : null
      : outcome;

  const { workspaceMember } = useAuth();
  const isSessionComplete = controlSession?.status === "complete";

  // Why no conclusion can be offered against this verdict, or null where one
  // can. Each reason is a rule the server holds too: the override route answers
  // 409 for a verdict something has already replaced, and requires `run_test`
  // for any write at all. An affordance offered past either is one the auditor
  // is only refused for after they have written their conclusion, and a
  // completed session is closed to new work by the same reasoning.
  //
  // It is a sentence rather than a flag because the pill still renders in all
  // three states — the verdict and its tone are what the header is for — so the
  // one thing that changes, its being inert, needs a reason a reader can find.
  const overrideUnavailableReason = !hasPermission(workspaceMember, "run_test")
    ? "You do not have permission to conclude on this cell."
    : isSessionComplete
      ? "This session is complete, so it cannot take a new conclusion."
      : // Whether the cell has moved on is read once, from `cellSuperseded` —
        // the same reader the pane's own supersession notice consumes — so this
        // gate and that notice cannot disagree about it. The verdict's own
        // `supersededAt` is this client's cached copy of that fact, and a
        // replacement written elsewhere never reaches this key, so it cannot be
        // trusted to be current: it reads null past the point the cell has
        // moved on. It is held alongside the live reader, not in place of it,
        // because once the 409 recovery refetches the detail it is the copy that
        // then holds the gate closed. Either being true is a genuine
        // supersession.
        cellSuperseded ||
          (analyticsVerdict != null && analyticsVerdict.supersededAt !== null)
        ? "A later conclusion replaced this one, so it cannot be concluded on."
        : null;

  // Result-level control-exception labels (the fail modes, e.g. "Improper
  // Authorization") surfaced beneath the trail conclusion. Only conclusive
  // failing results carry them.
  const controlExceptionLabels = useMemo(
    () =>
      attributeTestResult?.isConclusive && outcome === "fail"
        ? attributeTestResult.controlExceptions.map(
            (exception) => CONTROL_EXCEPTION_DESCRIPTIONS[exception],
          )
        : [],
    [attributeTestResult, outcome],
  );

  // Result-level mitigating-factors text shown beneath the exception pills.
  // Only conclusive results carry the field.
  const potentialMitigatingFactors =
    attributeTestResult?.isConclusive === true
      ? attributeTestResult.potentialMitigatingFactors
      : null;

  // Always reset to Workpaper Review on cell change.
  useEffect(() => {
    setMiddleView("review");
  }, [sourceRowId, checkResultId]);

  const reperformanceSteps = useMemo(
    () => attributeTestResult?.reperformanceSteps ?? [],
    [attributeTestResult],
  );

  const agentMode = columnKind === "agent" && middleView === "steps";
  const analyticsStepsMode =
    columnKind === "analytics" && middleView === "steps";

  /**
   * The step row a card belongs to, for the review tab's key-steps rows.
   *
   * Both middle tabs list the same run: the steps tab draws every operation and
   * the review tab draws the starred ones, and each drives this one selection
   * rather than a selection of its own — a step opened on one tab is the step
   * the other shows as open, and there is only ever one card in the viewer. The
   * card is what the two lists have in common, so its step id is what the row
   * is found by.
   */
  const rowForStepId = (stepId: string) =>
    deterministic.steps.rows.find((row) => row.card?.stepId === stepId) ?? null;

  // Every reperformance ("agent") citation, flattened in step order — the set
  // that drives the doc viewer's boxes while Agent Steps is shown.
  const agentCitations = useMemo(
    () => reperformanceSteps.flatMap((step) => step.citations),
    [reperformanceSteps],
  );

  // Switching views doesn't move any panes, but it re-points the doc viewer at
  // the new view's lead evidence: Agent Steps surfaces the first reperformance
  // citation; switching back to Workpaper Review restores the trail's opening
  // selection (same evidence a fresh cell click would show). An analytics
  // column's steps view points the viewer from the step the auditor picks, so
  // there is nothing to lead it with here.
  const handleMiddleViewChange = useCallback(
    (next: MiddlePaneView) => {
      setMiddleView(next);
      if (next === "review") {
        selectInitialEvidence();
        return;
      }
      if (columnKind === "analytics") return;
      const firstCitation = reperformanceSteps.find(
        (step) => step.citations.length > 0,
      )?.citations[0];
      if (firstCitation) handleCitationClick(firstCitation);
    },
    [
      columnKind,
      reperformanceSteps,
      handleCitationClick,
      selectInitialEvidence,
    ],
  );

  // After an import, jump to Workpaper Review and select the freshly-copied citation.
  // The selection is deferred until the refetched trail actually contains it
  // (below): selecting before then would let the nav's length-change effect
  // re-apply the stale index and yank focus to the previously-active citation.
  const [pendingReviewCitationId, setPendingReviewCitationId] = useState<
    string | null
  >(null);
  const handleImportToReview = useCallback(
    async (sourceCitationId: string, auditorStepId: string) => {
      const created = await importCitationToReview(
        sourceCitationId,
        auditorStepId,
      );
      setMiddleView("review");
      if (created.length === 0) return;
      // Prefer the screenshot copy for viewing, else the source.
      const screenshot = created.find((c) => isScreenshotDoc(c.documentId));
      const source = created.find((c) => !isScreenshotDoc(c.documentId));
      setPendingReviewCitationId((screenshot ?? source ?? created[0]).id);
    },
    [importCitationToReview, isScreenshotDoc],
  );

  // Select the just-imported citation once the refetched trail contains it — the
  // trail panel then highlights it and centres its step.
  useEffect(() => {
    if (!pendingReviewCitationId) return;
    const citation = allCitations.find((c) => c.id === pendingReviewCitationId);
    if (!citation) return;
    handleCitationClick(citation);
    setPendingReviewCitationId(null);
  }, [pendingReviewCitationId, allCitations, handleCitationClick]);

  // Tickmark view (PDF only): persisted toggle + `t` shortcut. Each viewer box
  // is tagged with its trail code + summary so the viewer can draw callouts.
  // Default tickmark view ON — only stay off if the user has explicitly opted
  // out (toggle writes "false"). A missing key (first visit) opens in tickmarks.
  const [showTickmarks, setShowTickmarks] = useState(
    () => localStorage.getItem(TICKMARKS_KEY) !== "false",
  );
  const toggleTickmarks = useCallback(() => {
    setShowTickmarks((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(TICKMARKS_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTextInputTarget(e.target)) return;
      if (e.key === "t") toggleTickmarks();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleTickmarks]);

  // citationId → { code, colour } — every L2 tickmark shares one blue so the
  // colour can't be misread as a pass/fail signal; the code distinguishes steps.
  const tickmarkMeta = useMemo(() => {
    const map = new Map<string, { code: string; color: string }>();
    if (!auditorTrail) return map;
    for (const step of buildTrailDisplay(
      auditorTrail,
      attributeColumnCode,
      isScreenshotDoc,
    )) {
      for (const item of step.items)
        for (const c of [item.source, item.screenshot])
          if (c?.id) map.set(c.id, { code: item.code, color: TRAIL_BOX_COLOR });
    }
    return map;
  }, [auditorTrail, attributeColumnCode, isScreenshotDoc]);

  // Review-trail boxes for the current document: coded + per-step coloured.
  const reviewViewerBoundingBoxes = useMemo(
    () =>
      actions.viewerBoundingBoxes?.map((b) => {
        const meta = b.id ? tickmarkMeta.get(b.id) : undefined;
        return {
          ...b,
          label: meta?.code,
          color: meta?.color,
          note:
            (b.id && allCitations.find((c) => c.id === b.id)?.shortSummary) ||
            undefined,
        };
      }),
    [actions.viewerBoundingBoxes, tickmarkMeta, allCitations],
  );

  // Agent boxes for the current document: uniform colour, no code (just a hover
  // summary). Built from the reperformance citations, mirroring the trail box
  // derivation in `useCitationActions` but scoped to the agent set.
  const agentViewerBoundingBoxes = useMemo(
    () =>
      citationsToViewerBoxes(agentCitations, actions.currentDocId)?.map(
        (box) => ({
          ...box,
          color: AGENT_BOX_COLOR,
          note:
            agentCitations.find((c) => c.id === box.id)?.shortSummary ||
            undefined,
        }),
      ),
    [agentCitations, actions.currentDocId],
  );

  // The viewer shows exactly one pane's evidence: agent boxes under Agent Steps,
  // review-trail boxes otherwise.
  const viewerBoundingBoxes = agentMode
    ? agentViewerBoundingBoxes
    : reviewViewerBoundingBoxes;

  // The active evidence row (source + optional screenshot) drives the viewer's
  // screenshot/source toggle. When the active row has a screenshot sibling, the
  // toggle swaps the shown document between the two views.
  const activeCitationId = navigation.currentBoundingBox?.citationId;

  // The selected-box index must point into whichever set the viewer is showing.
  const agentSelectedBoundingBoxIndex = useMemo(() => {
    if (!agentViewerBoundingBoxes || !activeCitationId) return undefined;
    const idx = agentViewerBoundingBoxes.findIndex(
      (b) => b.id === activeCitationId,
    );
    return idx >= 0 ? idx : undefined;
  }, [agentViewerBoundingBoxes, activeCitationId]);
  const selectedBoundingBoxIndex = agentMode
    ? agentSelectedBoundingBoxIndex
    : actions.selectedBoundingBoxIndex;

  // The agent citations in displayed order (grouped by step, source/screenshot
  // collapsed) — the sequence keyboard nav walks through in Agent Steps. The
  // grouping keeps steps that cite nothing, which flatten away to no items here,
  // so this list is unaffected by that.
  const agentViewList = useMemo(
    () =>
      groupAgentCitationsByStep(reperformanceSteps, isScreenshotDoc).flatMap(
        (group) => group.items,
      ),
    [reperformanceSteps, isScreenshotDoc],
  );

  // Keyboard nav over the agent citations while Agent Steps is shown; the trail's
  // handler stands down then, so the arrows aren't double-handled.
  useEvidenceArrowNav({
    items: agentViewList,
    activeCitationId,
    enabled: agentMode,
    onSelect: handleCitationClick,
  });

  const activeItem = useMemo(
    () => findTrailDisplayItem(auditorTrail, activeCitationId, isScreenshotDoc),
    [auditorTrail, activeCitationId, isScreenshotDoc],
  );
  const viewToggle = useMemo(() => {
    const screenshot = activeItem?.screenshot;
    if (!activeItem || !screenshot) return null;
    const source = activeItem.source;
    const sourceLabel =
      auditSession.documents.find((d) => d.documentId === source.documentId)
        ?.fetchableDocument?.fileType === "xlsx"
        ? "Excel"
        : "Source";
    return {
      mode:
        activeCitationId === screenshot.id
          ? ("screenshot" as const)
          : ("source" as const),
      sourceLabel,
      // Explicit toggle: show exactly the requested view, bypassing the
      // screenshot-default so "source" actually opens the live doc (e.g. xlsx).
      onSelect: (mode: "screenshot" | "source") =>
        showCitationView(mode === "screenshot" ? screenshot : source),
    };
  }, [activeItem, activeCitationId, auditSession.documents, showCitationView]);

  // The same screenshot/source toggle for Agent Steps: the agent display items
  // carry the source + screenshot pair (collapsed by encoded string), so an
  // excel-screenshot agent citation can flip between the rendered image and the
  // live xlsx exactly like Workpaper Review.
  const agentActiveItem = useMemo(
    () =>
      agentViewList.find(
        (item) =>
          item.source.id === activeCitationId ||
          item.screenshot?.id === activeCitationId,
      ) ?? null,
    [agentViewList, activeCitationId],
  );
  const agentViewToggle = useMemo(() => {
    const screenshot = agentActiveItem?.screenshot;
    if (!agentActiveItem || !screenshot) return null;
    const source = agentActiveItem.source;
    const sourceLabel =
      auditSession.documents.find((d) => d.documentId === source.documentId)
        ?.fetchableDocument?.fileType === "xlsx"
        ? "Excel"
        : "Source";
    return {
      mode:
        activeCitationId === screenshot.id
          ? ("screenshot" as const)
          : ("source" as const),
      sourceLabel,
      onSelect: (mode: "screenshot" | "source") =>
        showCitationView(mode === "screenshot" ? screenshot : source),
    };
  }, [
    agentActiveItem,
    activeCitationId,
    auditSession.documents,
    showCitationView,
  ]);

  // Box-trash in the viewer removes only the highlight (keeps the citation).
  // `handleRemoveBoundingBox` clears the active selection, which would hide the
  // screenshot/source toggle and make a re-add error "Citation not found"
  // (handleLinkingComplete keys off the selected citation). So re-select the SAME
  // citation with its box cleared: the citation stays focused, the current view
  // stays put (e.g. the screenshot pdf, not flipping to the xlsx), the toggle
  // persists, and a re-drawn annotation re-attaches to it.
  const handleRemoveTrailBox = useCallback(
    (target: { id?: string; index: number }) => {
      const removedId = target.id ?? allCitations[target.index]?.id;
      const removed = allCitations.find((c) => c.id === removedId);
      const removal = actions.handleRemoveBoundingBox(target);
      if (removed) {
        navigation.setCurrentBoundingBox({
          citationId: removed.id,
          elementId: removed.encodedCitationString || undefined,
        });
      }
      return removal;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allCitations,
      actions.handleRemoveBoundingBox,
      navigation.setCurrentBoundingBox,
    ],
  );

  // Navigate to another sample/attribute from the summary grid — re-targets the
  // fold-out (same path as clicking a work-paper cell).
  const handleNavigate = (target: {
    sourceRowId: string;
    checkResultId: string;
  }) =>
    setCurrentSelection({
      procedureSessionId: auditSession.id,
      sourceRowId: target.sourceRowId,
      checkResultId: target.checkResultId,
    });

  // The attribute-test-column code + name + sample, truncated with a tooltip
  // showing the full text on hover (column names can be long). An analytics cell
  // carries neither a code nor an attribute-test column, so its name comes from
  // the verdict's own column — "Attribute" over a workflow's verdict names a
  // thing the cell is not.
  const headerLabel = `${attributeColumnCode ? `${attributeColumnCode} - ` : ""}${
    columnName || analytics.columnName || "Attribute"
  }${sourceRow?.sampleNumber ? ` • Sample ${sourceRow.sampleNumber}` : ""}`;

  // A bordered, hoverable chip that reads as clickable and opens the samples ×
  // attributes navigation dropdown anchored beneath it.
  const navTrigger = (
    <button
      type="button"
      title={headerLabel}
      className="flex min-w-0 items-center gap-1.5 rounded-md border border-stone-200 bg-white/70 px-2 py-1 text-left transition-colors hover:border-stone-300 hover:bg-white"
    >
      <span className="min-w-0 truncate font-medium">{headerLabel}</span>
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-stone-400" />
    </button>
  );

  const title = (
    <div className="flex w-full min-w-0 flex-col gap-1">
      <div className="flex w-full min-w-0 items-center gap-2">
        {controlSession ? (
          <WorkpaperNavGrid
            procedureSessionId={auditSession.id}
            controlSession={controlSession}
            onNavigate={handleNavigate}
            trigger={navTrigger}
            currentSourceRowId={sourceRowId}
            currentAttributeTestResultId={attributeTestResult?.id}
          />
        ) : (
          navTrigger
        )}
        {columnKind === "analytics"
          ? analyticsVerdict && (
              /* The pill opens the override rather than writing an outcome. A
                 conclusion needs the auditor's own words beside their answer —
                 an outcome sent alone would be signed with the judge's — so the
                 only thing this control can do is ask for both. */
              <AnalyticsOutcomeBadge
                verdict={analyticsVerdict}
                unavailableReason={overrideUnavailableReason}
                onOverride={analyticsOverride.open}
              />
            )
          : attributeTestResult && (
              <OutcomeBadgeSelect
                outcome={outcome}
                isConclusive={isConclusive}
                onSelect={(value) =>
                  actions.handleUpdateField("outcome", value)
                }
              />
            )}
      </div>
    </div>
  );

  return (
    <>
      <WorkpaperFoldOut
        title={title}
        middleTopBar={
          <div className="flex shrink-0 items-center justify-center border-b border-stone-200 bg-white px-3 py-2">
            <MiddlePaneViewToggle
              value={middleView}
              onChange={handleMiddleViewChange}
              columnKind={columnKind}
            />
          </div>
        }
        onClose={onClose}
        headerTone={
          headerOutcome === "pass"
            ? "pass"
            : headerOutcome === "fail"
              ? "fail"
              : "neutral"
        }
        gridExpanded={gridExpanded}
        onGridExpandedChange={onGridExpandedChange}
        paneLayout={paneLayout}
        gridScrollTopRef={gridScrollTopRef}
        onPaneLayoutChange={onPaneLayoutChange}
        gridPane={gridPane}
        middlePane={
          middleView === "review" ? (
            columnKind === "analytics" ? (
              <AnalyticsReviewTab
                auditSession={auditSession}
                sourceRowId={sourceRowId}
                analyticsResult={analytics.analyticsResult}
                column={analytics.column}
                isLoading={analytics.isLoading}
                // The verdict read is disabled until the selection names a
                // verdict id; a disabled read is not an empty result, so the tab
                // waits rather than denying a verdict it never asked for.
                issued={analytics.analyticsResultId !== undefined}
                error={analytics.error}
                onRetry={() => void analytics.refetch()}
                selectedStepId={deterministic.selected?.card?.stepId ?? null}
                onSelectStep={(stepId) => {
                  const row = rowForStepId(stepId);
                  if (row) {
                    selectStep(row);
                  }
                }}
                // The star decides what the exported workpaper holds, so it is
                // a write, and a complete session takes none — the same rule
                // the citation star in the viewer pane holds itself to. Withheld
                // rather than refused: the row draws a read-only star, which
                // still says whether the step is in the workpaper.
                //
                // It writes to the card's own citation, handed up by the row, so
                // the write lands whether or not the run-steps read a step row is
                // composed from has answered — the read that is empty in flight
                // and empty on failure, which a row lookup would silently swallow
                // the press against.
                onToggleStar={
                  isSessionComplete
                    ? undefined
                    : (citation) =>
                        void deterministic.toggleStarForCitation(citation)
                }
                outputFiles={outputFiles}
              />
            ) : (
              <AuditorTrailPanel
                auditorTrail={auditorTrail}
                attributeCode={attributeColumnCode}
                onCitationClick={handleCitationClick}
                onToggleInWorkpaper={actions.handleToggleKeyForCitation}
                activeCitationId={activeCitationId}
                isScreenshotDoc={isScreenshotDoc}
                inconclusive={attributeTestResult != null && !isConclusive}
                onSetStepStatus={actions.handleSetStepException}
                onSetL1={actions.handleSetTrailConclusion}
                onSetStepText={actions.handleSetStepText}
                onUpdateCitationSummary={actions.handleUpdateCitationSummary}
                onDeleteCitation={(citation) =>
                  actions.handleDeleteCitation({
                    id: citation.id,
                    index: allCitations.findIndex((c) => c.id === citation.id),
                  })
                }
                attributeTestResultId={attributeTestResult?.id}
                controlExceptions={controlExceptionLabels}
                potentialMitigatingFactors={potentialMitigatingFactors}
              />
            )
          ) : analyticsStepsMode ? (
            cellSuperseded ? (
              // The cell has moved on; the steps below would be the new run's,
              // framed by the old verdict's header. It collapses
              // to the review tab's notice, so both tabs agree the cell moved
              // on and the auditor reopens to see the new run properly framed.
              <div className={REVIEW_MESSAGE_CLASS}>{NEWER_RESULT_NOTICE}</div>
            ) : (
              <DeterministicStepsPanel
                steps={deterministic.steps}
                population={deterministic.population}
                isLoading={deterministic.isLoading}
                stepsUnreadable={deterministic.stepsUnreadable}
                workflowDeletion={deterministic.workflowDeletion}
                selectedKey={deterministic.selected?.key ?? null}
                onSelectStep={selectStep}
                // Withheld on a complete session, as on the review tab: the two
                // tabs write the same flag, so gating one and not the other
                // leaves a read-only star beside a live one that changes it.
                onToggleStar={
                  isSessionComplete
                    ? undefined
                    : (row) => void deterministic.toggleStar(row)
                }
              />
            )
          ) : (
            <AgentStepsPanel
              reperformanceSteps={reperformanceSteps}
              onViewEvidence={handleCitationClick}
              activeCitationId={activeCitationId}
              isScreenshotDoc={isScreenshotDoc}
              onImportToReview={handleImportToReview}
              reviewSteps={reviewStepOptions}
            />
          )
        }
        viewerPane={
          // Shown off the open flag, because a step pick closes the file
          // without dropping it, so the step's row keeps the focus it was
          // given. Withheld from a superseded cell or a deleted workflow in
          // the render that reports it, before the effect above has closed
          // the viewer.
          columnKind === "analytics" &&
          !cellSuperseded &&
          !workflowDeleted &&
          outputFiles.viewerOpen &&
          outputFiles.viewed ? (
            <AnalyticsOutputViewerPane
              viewed={outputFiles.viewed}
              onClose={closeOutputFilePane}
              onDownload={outputFiles.download}
              pendingFileIds={outputFiles.pendingFileIds}
            />
          ) : columnKind === "analytics" ? (
            // Both of an analytics column's middle views select steps, and a
            // step's only evidence is the card the server rendered for it:
            // there are no citations over a document to preview, so the doc
            // viewer would stand there inviting a selection nothing in this
            // column can make. The viewer states its own empty case until a
            // step is picked.
            <StepCardViewer
              procedureSessionId={auditSession.id}
              step={deterministic.selected}
              // Handed over on both middle views, not just the steps tab: the
              // review pane selects steps into this same viewer, and those
              // cards outlive a deletion the pane must still state.
              workflowDeletion={deterministic.workflowDeletion}
              onToggleStar={async () => {
                const step = deterministic.selected;
                if (step) await deterministic.toggleStar(step);
              }}
            />
          ) : (
            <DocumentViewerPanel
              showDocumentViewer={showDocumentViewer}
              currentBoundingBox={navigation.currentBoundingBox}
              viewerBoundingBoxes={viewerBoundingBoxes}
              showTickmarks={showTickmarks}
              onToggleTickmarks={toggleTickmarks}
              selectedBoundingBoxId={actions.selectedBoundingBoxId}
              selectedBoundingBoxIndex={selectedBoundingBoxIndex}
              currentSelectionValue={navigation.currentSelectionValue}
              allCitations={allCitations}
              // Agent Steps is read-only: agent citations are the model's raw
              // evidence, not editable trail entries. Boxes stay clickable
              // (highlight only) via `onSelect`.
              allowEditing={!agentMode}
              allowKeyToggle={!agentMode}
              onLinkingComplete={actions.handleLinkingComplete}
              onDelete={handleRemoveTrailBox}
              onSelect={({ id }) => {
                if (!id) return;
                const pool = agentMode ? agentCitations : allCitations;
                const clicked = pool.find((c) => c.id === id);
                if (!clicked) return;
                navigation.setCurrentBoundingBox(
                  citationToBoundingBox(clicked),
                );
                navigation.setCurrentSelectionValue(
                  clicked.shortSummary || undefined,
                );
              }}
              onToggleKeyCitation={actions.handleToggleKeyCitation}
              onAddCitation={
                agentMode ? undefined : () => trailAddCitation.setOpen(true)
              }
              viewToggle={agentMode ? agentViewToggle : viewToggle}
            />
          )
        }
      />
      <AddCitationModal
        open={trailAddCitation.open}
        onOpenChange={trailAddCitation.setOpen}
        auditSession={auditSession}
        isAttributeTestColumn={true}
        reperformanceSteps={[]}
        auditorSteps={auditorTrail?.steps ?? []}
        onCreateCitation={trailAddCitation.handleCreateCitation}
        onUploadCitationCreated={trailAddCitation.handleUploadCreated}
      />
      {/* Mounted beside the fold-out rather than inside its header, so the
          override's own re-read of the verdict — which flips the cache entry to
          loading and notifies synchronously — cannot tear the dialog down with
          the auditor's typed conclusion still in it. */}
      <AnalyticsOverrideDialog flow={analyticsOverride} />
    </>
  );
}
